// Admin — Inventory Reports suite
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Package, AlertTriangle, TrendingUp, PackageX, Clock, BarChart3, Download, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { k: 'stock-on-hand', l: 'Stock On Hand', icon: Package },
  { k: 'valuation', l: 'Valuation', icon: BarChart3 },
  { k: 'low-stock', l: 'Low Stock', icon: AlertTriangle },
  { k: 'negative-stock', l: 'Negative Stock', icon: PackageX },
  { k: 'top-movers', l: 'Top Movers', icon: TrendingUp },
  { k: 'dead-stock', l: 'Dead Stock', icon: Clock },
];

export default function AdminInventoryReports() {
  const { token } = useAuth();
  const [active, setActive] = useState('stock-on-hand');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [siteId, setSiteId] = useState('');
  const [category, setCategory] = useState('');
  const [dateFrom, setDateFrom] = useState(new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [deadDays, setDeadDays] = useState(60);
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/api/sites`, { headers }).then(r => r.json()).then(d => setSites((d.items || d || []).filter(s => s.site_type !== 'warehouse')));
    fetch(`${API}/api/products/categories`, { headers }).then(r => r.json()).then(d => setCategories(d.categories || []));
  }, []);

  useEffect(() => { fetchReport(); }, [active, search, siteId, category, dateFrom, dateTo, deadDays]);

  const fetchReport = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (siteId) params.append('site_id', siteId);
    if (active === 'stock-on-hand') {
      if (search) params.append('search', search);
      if (category) params.append('category', category);
    }
    if (active === 'valuation' && category) params.append('category', category);
    if (active === 'top-movers') {
      params.append('date_from', dateFrom);
      params.append('date_to', dateTo);
      params.append('limit', 100);
    }
    if (active === 'dead-stock') params.append('days', deadDays);
    const res = await fetch(`${API}/api/inventory-reports/${active}?${params}`, { headers });
    setData(await res.json());
    setLoading(false);
  };

  const exportCsv = () => {
    let rows, header;
    if (active === 'valuation') {
      header = ['Category', 'Product Count', 'Qty', 'Cost Value', 'Retail Value', 'Potential Margin'];
      rows = (data?.by_category || []).map(r => [r.category, r.product_count, r.total_qty, r.total_value_cost.toFixed(2), r.total_value_retail.toFixed(2), r.potential_margin.toFixed(2)]);
    } else {
      const items = data?.items || [];
      if (!items.length) return;
      header = Object.keys(items[0]);
      rows = items.map(r => header.map(k => r[k] ?? ''));
    }
    const csv = [header.join(','), ...rows.map(r => r.map(v => typeof v === 'string' && v.includes(',') ? `"${v.replace(/"/g, '""')}"` : v).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `${active}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="p-6 min-h-screen bg-uf-bg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Inventory Reports</h1>
          <p className="text-uf-textMuted text-sm mt-1">Stock on hand · valuation · low stock · movers · dead stock</p>
        </div>
        <button onClick={exportCsv} disabled={loading || !data} data-testid="inv-export-csv"
                className="px-3 py-2 bg-uf-red text-white rounded text-sm flex items-center gap-2 disabled:opacity-40">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4 border-b border-uf-border pb-3">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.k} onClick={() => setActive(t.k)} data-testid={`inv-tab-${t.k}`}
                    className={`px-3 py-2 text-sm font-medium rounded flex items-center gap-2 ${active === t.k ? 'bg-uf-red text-white' : 'bg-uf-surface border border-uf-border text-uf-textMuted hover:text-white'}`}>
              <Icon className="w-4 h-4" /> {t.l}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {active === 'stock-on-hand' && (
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-3 text-uf-textMuted" />
            <input placeholder="Search name or SKU..." value={search} onChange={e => setSearch(e.target.value)}
                   data-testid="inv-search" className="w-full bg-uf-surface border border-uf-border pl-10 pr-4 py-2 text-white text-sm rounded" />
          </div>
        )}
        {(active === 'stock-on-hand' || active === 'valuation') && (
          <select value={category} onChange={e => setCategory(e.target.value)} data-testid="inv-category-filter" className="bg-uf-surface border border-uf-border px-3 py-2 text-white text-sm rounded">
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {['low-stock', 'top-movers', 'dead-stock'].includes(active) && (
          <select value={siteId} onChange={e => setSiteId(e.target.value)} data-testid="inv-site-filter" className="bg-uf-surface border border-uf-border px-3 py-2 text-white text-sm rounded">
            <option value="">All sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        {active === 'top-movers' && <>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="inv-date-from" className="bg-uf-surface border border-uf-border px-3 py-2 text-white text-sm rounded" />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="inv-date-to" className="bg-uf-surface border border-uf-border px-3 py-2 text-white text-sm rounded" />
        </>}
        {active === 'dead-stock' && (
          <select value={deadDays} onChange={e => setDeadDays(e.target.value)} data-testid="inv-dead-days" className="bg-uf-surface border border-uf-border px-3 py-2 text-white text-sm rounded">
            <option value="30">No sale in 30d</option>
            <option value="60">No sale in 60d</option>
            <option value="90">No sale in 90d</option>
            <option value="180">No sale in 180d</option>
          </select>
        )}
      </div>

      {/* Content */}
      {loading && <div className="bg-uf-surface border border-uf-border rounded p-8 text-center text-uf-textMuted">Loading…</div>}

      {!loading && active === 'stock-on-hand' && <StockTable items={data?.items || []} />}
      {!loading && active === 'valuation' && <Valuation data={data} />}
      {!loading && active === 'low-stock' && <LowStock items={data?.items || []} />}
      {!loading && active === 'negative-stock' && <NegativeStock items={data?.items || []} />}
      {!loading && active === 'top-movers' && <TopMovers items={data?.items || []} />}
      {!loading && active === 'dead-stock' && <DeadStock items={data?.items || []} days={deadDays} />}
    </div>
  );
}

// ---------- Report renderers ----------
function Table({ columns, items, testidPrefix, empty = 'No data' }) {
  if (!items?.length) return <div className="bg-uf-surface border border-uf-border rounded p-8 text-center text-uf-textMuted">{empty}</div>;
  return (
    <div className="bg-uf-surface border border-uf-border rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-uf-bg text-uf-textMuted text-xs uppercase tracking-wider">
            {columns.map(c => <th key={c.k} className={`px-3 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'} font-bold`}>{c.l}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) =>
            <tr key={it.product_id || i} data-testid={`${testidPrefix}-${i}`} className="border-t border-uf-border hover:bg-uf-bg">
              {columns.map(c => {
                const v = it[c.k];
                const rendered = c.fmt ? c.fmt(v, it) : v;
                return <td key={c.k} className={`px-3 py-2 ${c.align === 'right' ? 'text-right font-mono' : ''} text-white whitespace-nowrap`}>{rendered ?? '—'}</td>;
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
const money = v => v != null ? `£${v.toFixed(2)}` : '—';

function StockTable({ items }) {
  const total = items.reduce((s, r) => s + r.stock_value, 0);
  return <>
    <div className="mb-3 text-uf-textMuted text-sm">{items.length} products · total stock value <span className="text-white font-bold">{money(total)}</span></div>
    <Table testidPrefix="soh" items={items} columns={[
      { k: 'name', l: 'Product' },
      { k: 'sku', l: 'SKU' },
      { k: 'category', l: 'Category' },
      { k: 'quantity', l: 'Qty', align: 'right' },
      { k: 'cost_price', l: 'Cost £', align: 'right', fmt: v => money(v) },
      { k: 'stock_value', l: 'Stock Value £', align: 'right', fmt: v => money(v) },
      { k: 'retail_price', l: 'Retail £', align: 'right', fmt: v => money(v) },
    ]} />
  </>;
}

function Valuation({ data }) {
  if (!data?.grand_total) return null;
  const g = data.grand_total;
  return <>
    <div className="grid grid-cols-4 gap-3 mb-4">
      <Kpi label="Products" value={g.product_count} />
      <Kpi label="Total units" value={g.total_qty?.toLocaleString()} />
      <Kpi label="Cost value" value={money(g.total_value_cost)} />
      <Kpi label="Potential margin" value={money(g.potential_margin)} accent />
    </div>
    <Table testidPrefix="val" items={data.by_category} columns={[
      { k: 'category', l: 'Category' },
      { k: 'product_count', l: 'Products', align: 'right' },
      { k: 'total_qty', l: 'Units', align: 'right' },
      { k: 'total_value_cost', l: 'Cost Value', align: 'right', fmt: v => money(v) },
      { k: 'total_value_retail', l: 'Retail Value', align: 'right', fmt: v => money(v) },
      { k: 'potential_margin', l: 'Margin', align: 'right', fmt: v => money(v) },
    ]} />
  </>;
}

function LowStock({ items }) {
  return <>
    <div className="mb-3 text-uf-textMuted text-sm">{items.length} products at or below reorder level</div>
    <Table testidPrefix="low" items={items} empty="No products below reorder level — all good."
      columns={[
        { k: 'name', l: 'Product' },
        { k: 'sku', l: 'SKU' },
        { k: 'category', l: 'Category' },
        { k: 'stock_quantity', l: 'Current', align: 'right' },
        { k: 'reorder_level', l: 'Reorder at', align: 'right' },
        { k: 'deficit', l: 'Deficit', align: 'right' },
        { k: 'suggested_order_qty', l: 'Suggested Order', align: 'right' },
      ]} />
  </>;
}

function NegativeStock({ items }) {
  return <>
    <div className="mb-3 text-uf-textMuted text-sm">{items.length} products with negative stock — investigate.</div>
    <Table testidPrefix="neg" items={items} empty="No negative stock — data clean."
      columns={[
        { k: 'name', l: 'Product' },
        { k: 'sku', l: 'SKU' },
        { k: 'category', l: 'Category' },
        { k: 'stock_quantity', l: 'Stock', align: 'right' },
      ]} />
  </>;
}

function TopMovers({ items }) {
  return <>
    <div className="mb-3 text-uf-textMuted text-sm">Top {items.length} outbound movers in the selected period</div>
    <Table testidPrefix="top" items={items} empty="No sales in this period."
      columns={[
        { k: 'name', l: 'Product' },
        { k: 'sku', l: 'SKU' },
        { k: 'category', l: 'Category' },
        { k: 'total_qty', l: 'Sold', align: 'right' },
        { k: 'txn_count', l: 'Transactions', align: 'right' },
        { k: 'total_value', l: 'Value', align: 'right', fmt: v => money(Math.abs(v)) },
      ]} />
  </>;
}

function DeadStock({ items, days }) {
  const total = items.reduce((s, r) => s + r.stuck_value, 0);
  return <>
    <div className="mb-3 text-uf-textMuted text-sm">{items.length} products with stock on hand but no sale in {days} days · <span className="text-white font-bold">{money(total)}</span> locked up</div>
    <Table testidPrefix="dead" items={items} empty="Nothing sitting idle — inventory is turning over."
      columns={[
        { k: 'name', l: 'Product' },
        { k: 'sku', l: 'SKU' },
        { k: 'category', l: 'Category' },
        { k: 'stock_quantity', l: 'Stock', align: 'right' },
        { k: 'cost_price', l: 'Cost £', align: 'right', fmt: v => money(v) },
        { k: 'stuck_value', l: 'Stuck Value £', align: 'right', fmt: v => money(v) },
        { k: 'days_since_sale', l: 'Days since sale', align: 'right', fmt: v => v != null ? v : 'never' },
      ]} />
  </>;
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-uf-surface border border-uf-border rounded p-4">
      <div className="text-xs text-uf-textMuted uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-heading font-bold mt-1 ${accent ? 'text-uf-red' : 'text-white'}`}>{value ?? '—'}</div>
    </div>
  );
}
